import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { worldApi, type WorldStatus } from '../lib/api';
import { useSounds } from '../lib/sounds';
import {
  PortalScene,
  ArtifactCard,
  LetterScroll,
  LetterFocusView,
  InstructionsModal,
  InstructionsButton,
} from '../components/portal';
import { VersionInfo } from '../components/VersionInfo';

interface Letter {
  id: string;
  direction: 'in' | 'out';
  subject: string | null;
  preview: string;
  date: string;
  type?: string;
  thumbnailUrl?: string | null;
}

interface SelectedLetter {
  id: string;
  direction: string;
  subject: string | null;
  content: string;
  date: string;
}

/**
 * Main dashboard page - immersive portal design
 * Shows correspondent address and received letters as scrolls
 */
export function DashboardPage() {
  const { logout } = useAuth();
  const { play } = useSounds();
  const [worldStatus, setWorldStatus] = useState<WorldStatus | null>(null);
  const [letters, setLetters] = useState<Letter[]>([]);
  const [selectedLetter, setSelectedLetter] = useState<SelectedLetter | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLetters, setIsLoadingLetters] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Copy address feedback state
  const [copied, setCopied] = useState(false);

  // Instructions modal state
  const [showInstructions, setShowInstructions] = useState(false);

  // Compose modal state (hidden feature for testing)
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composeSuccess, setComposeSuccess] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const status = await worldApi.getStatus();
        setWorldStatus(status);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load world status');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStatus();
  }, []);

  // Fetch and combine all letters into a single feed
  useEffect(() => {
    const fetchLetters = async () => {
      if (!worldStatus) return;
      setIsLoadingLetters(true);
      try {
        const [receivedResult, sentResult] = await Promise.all([
          worldApi.getLetters({ type: 'out', letterType: 'daily', limit: 20 }),
          worldApi.getLetters({ type: 'in', limit: 20 }),
        ]);
        // Combine and sort by date (newest first)
        const combined = [...receivedResult.letters, ...sentResult.letters].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setLetters(combined);
      } catch (err) {
        console.error('Failed to load letters:', err);
      } finally {
        setIsLoadingLetters(false);
      }
    };

    fetchLetters();
  }, [worldStatus]);

  const handleLogout = async () => {
    await logout();
  };

  const handleCopyAddress = async () => {
    if (!worldStatus) return;
    play('button-click');
    try {
      await navigator.clipboard.writeText(worldStatus.world.inboundAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  const handleSelectLetter = async (letter: Letter) => {
    play('letter-open');
    try {
      const detail = await worldApi.getLetter(letter.direction, letter.id);
      setSelectedLetter(detail);
    } catch (err) {
      console.error('Failed to load letter:', err);
    }
  };

  const handleCloseLetter = () => {
    play('letter-close');
    setSelectedLetter(null);
  };

  const handleOpenCompose = () => {
    setComposeSubject('');
    setComposeBody('');
    setComposeError(null);
    setComposeSuccess(false);
    setIsComposeOpen(true);
  };

  const handleCloseCompose = () => {
    play('modal-close');
    setIsComposeOpen(false);
    setComposeSubject('');
    setComposeBody('');
    setComposeError(null);
    setComposeSuccess(false);
  };

  const handleSubmitCompose = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!composeBody.trim()) {
      setComposeError('Please write something in your letter.');
      return;
    }

    setIsComposing(true);
    setComposeError(null);

    try {
      await worldApi.composeLetter(composeSubject.trim() || undefined, composeBody.trim());
      play('letter-send');
      setComposeSuccess(true);
      // Refresh letters list after a short delay
      setTimeout(async () => {
        const [receivedResult, sentResult] = await Promise.all([
          worldApi.getLetters({ type: 'out', letterType: 'daily', limit: 20 }),
          worldApi.getLetters({ type: 'in', limit: 20 }),
        ]);
        const combined = [...receivedResult.letters, ...sentResult.letters].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        setLetters(combined);
        // Close without playing modal-close since we just played letter-send
        setIsComposeOpen(false);
        setComposeSubject('');
        setComposeBody('');
        setComposeError(null);
        setComposeSuccess(false);
      }, 1500);
    } catch (err) {
      setComposeError(err instanceof Error ? err.message : 'Failed to send letter');
    } finally {
      setIsComposing(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <PortalScene align="center">
        <div className="text-mist animate-pulse-slow">The world stirs...</div>
      </PortalScene>
    );
  }

  // Error state
  if (error) {
    return (
      <PortalScene align="center">
        <ArtifactCard>
          <div className="text-center">
            <div className="text-ember mb-4">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="text-rune hover:text-rune-glow underline text-sm"
            >
              Try again
            </button>
          </div>
        </ArtifactCard>
      </PortalScene>
    );
  }

  if (!worldStatus) return null;

  return (
    <PortalScene align="center">
      {/* Main content container */}
      <div className="w-full max-w-xl space-y-4 sm:space-y-6 pb-28 sm:pb-24 px-2 sm:px-0">
        {/* Correspondent Card - Centerpiece */}
        <ArtifactCard>
          <div className="text-center">
            <p className="text-mist/70 text-xs uppercase tracking-widest mb-2">
              Your Correspondent
            </p>
            <h2 className="font-display text-2xl text-parchment mb-4">
              {worldStatus.world.correspondentName}
            </h2>
            <div className="inline-flex items-center gap-2 bg-void/50 border border-rune/20 rounded-lg px-4 sm:px-6 py-3 max-w-full">
              <code className="text-rune text-base sm:text-lg font-mono break-all">
                {worldStatus.world.inboundAddress}
              </code>
              <button
                onClick={handleCopyAddress}
                className="flex-shrink-0 p-1.5 rounded text-mist/50 hover:text-rune hover:bg-rune/10 transition-colors"
                aria-label="Copy address"
                title={copied ? 'Copied!' : 'Copy address'}
              >
                {copied ? (
                  <svg
                    className="w-4 h-4 text-success"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-mist/50 text-sm mt-4 max-w-sm mx-auto">
              Send your letters to this address. The world awaits your words.
            </p>
            {/* Chronicle info */}
            <div className="mt-6 pt-4 border-t border-stone/20">
              <div className="flex justify-center gap-6 text-xs">
                <div className="text-center">
                  <div className="text-mist/40 uppercase tracking-wider mb-1">Chronicle Day</div>
                  <div className="text-parchment/70 font-display">{worldStatus.world.dayIndex}</div>
                </div>
                <div className="text-center">
                  <div className="text-mist/40 uppercase tracking-wider mb-1">Replies Arrive</div>
                  <div className="text-parchment/70 font-display">
                    {worldStatus.world.deliveryTime}
                  </div>
                </div>
                {worldStatus.stamps && (
                  <div className="text-center">
                    <div className="text-mist/40 uppercase tracking-wider mb-1">Postal Stamps</div>
                    <div
                      className={`font-display ${
                        worldStatus.stamps.remaining < 100 ? 'text-ember' : 'text-parchment/70'
                      }`}
                    >
                      {worldStatus.stamps.remaining.toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
              {worldStatus.stamps && worldStatus.stamps.remaining < 1000 && (
                <div className="text-center mt-4">
                  <a
                    href="/billing"
                    className="text-rune text-xs hover:text-rune-glow underline transition-colors"
                  >
                    Upgrade for more stamps
                  </a>
                </div>
              )}
            </div>
          </div>
        </ArtifactCard>

        {/* Paused State Banner */}
        {worldStatus.world.status === 'paused' && worldStatus.stamps && (
          <ArtifactCard>
            <div className="text-center py-6">
              <div className="text-ember mb-2 text-lg font-display">Chronicle Paused</div>
              <p className="text-mist/70 text-sm max-w-md mx-auto mb-3">
                Your postal stamps have been depleted for this month. Your correspondent has sent a
                farewell letter and will resume writing when stamps refill.
              </p>
              <p className="text-mist/50 text-xs">
                Stamps refill on{' '}
                {new Date(worldStatus.stamps.periodEnds).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>
          </ArtifactCard>
        )}

        {/* Suspended State Banner */}
        {worldStatus.world.status === 'suspended' && (
          <ArtifactCard>
            <div className="text-center py-6">
              <div className="text-ember mb-2 text-lg font-display">Chronicle Suspended</div>
              <p className="text-mist/70 text-sm max-w-md mx-auto">
                Your chronicle has been suspended. Please contact support for assistance.
              </p>
            </div>
          </ArtifactCard>
        )}

        {/* Letter Box - Combined Scrolls */}
        {isLoadingLetters ? (
          <div className="text-center py-8">
            <div className="text-mist/60 animate-pulse">Gathering scrolls...</div>
          </div>
        ) : letters.length > 0 ? (
          <div className="relative rounded-xl overflow-hidden">
            {/* Wooden box - base color */}
            <div
              className="absolute inset-0"
              style={{ backgroundColor: '#5a3a28' }}
            />
            {/* Horizontal wood planks */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `
                  repeating-linear-gradient(
                    180deg,
                    #5a3a28 0px,
                    #5c3d2e 4px,
                    #5a3a28 36px,
                    #4d3020 38px,
                    #5a3a28 40px
                  )
                `,
              }}
            />
            {/* Subtle wood grain */}
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `
                  repeating-linear-gradient(
                    90deg,
                    transparent 0px,
                    rgba(80, 50, 30, 0.3) 150px,
                    transparent 200px
                  )
                `,
              }}
            />
            {/* Inner shadow for depth */}
            <div className="absolute inset-0 rounded-xl shadow-[inset_0_3px_12px_rgba(0,0,0,0.5),inset_0_-3px_10px_rgba(0,0,0,0.4),inset_3px_0_8px_rgba(0,0,0,0.2),inset_-3px_0_8px_rgba(0,0,0,0.2)]" />

            {/* Content */}
            <div className="relative p-5">
              {/* Header with compose button */}
              <div className="flex items-center justify-between mb-4">
                <div className="w-8" /> {/* Spacer for centering */}
                <h3 className="font-display text-lg text-amber-100 drop-shadow-md">
                  Letterbox
                </h3>
                <button
                  onClick={handleOpenCompose}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-900/40 hover:bg-amber-900/60 text-amber-100/80 hover:text-amber-100 transition-colors"
                  title="Write a letter"
                >
                  <svg className="w-5 h-5 -rotate-45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5l6.74-6.76z" />
                    <line x1="16" y1="8" x2="2" y2="22" />
                    <line x1="17.5" y1="15" x2="9" y2="15" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3">
                {letters.map((letter, index) => (
                  <LetterScroll
                    key={letter.id}
                    subject={letter.subject}
                    preview={letter.preview}
                    date={letter.date}
                    isSelected={selectedLetter?.id === letter.id}
                    onClick={() => handleSelectLetter(letter)}
                    index={index}
                    thumbnailUrl={letter.thumbnailUrl}
                    direction={letter.direction}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <ArtifactCard>
            <div className="text-center py-6">
              <h3 className="font-display text-lg text-parchment mb-3">Letterbox</h3>
              <p className="text-mist/60 font-serif italic">
                Empty, for now...
              </p>
              <p className="text-mist/40 text-sm mt-2">
                Your first letter will awaken the world.
              </p>
            </div>
          </ArtifactCard>
        )}
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-void via-void/98 to-transparent pt-10 pb-4">
        <div className="text-center space-y-2">
          <button
            onClick={handleLogout}
            className="text-mist/80 hover:text-parchment text-sm transition-colors"
          >
            Leave the Portal
          </button>
          <div className="flex items-center justify-center gap-2 flex-wrap px-4">
            <a href="/billing" className="text-mist/50 hover:text-rune text-xs transition-colors">
              billing
            </a>
            <span className="text-mist/30">·</span>
            <InstructionsButton onClick={() => setShowInstructions(true)} />
            <span className="text-mist/30">·</span>
            <VersionInfo />
          </div>
        </div>
      </div>

      {/* Selected Letter Focus View */}
      <LetterFocusView
        content={selectedLetter?.content ?? ''}
        subject={selectedLetter?.subject}
        date={selectedLetter?.date}
        isOpen={!!selectedLetter}
        onClose={handleCloseLetter}
      />

      {/* Compose - Immersive parchment writing */}
      {isComposeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
          <div
            className="absolute inset-0 bg-black/80"
            onClick={handleCloseCompose}
          />
          <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Parchment paper */}
            <div
              className="absolute inset-0"
              style={{
                background: `
                  linear-gradient(145deg,
                    #f8edd5 0%,
                    #f3e4c6 20%,
                    #ecdbb5 50%,
                    #f5e2c4 80%,
                    #f0dfc2 100%
                  )
                `,
                boxShadow: `
                  0 4px 24px rgba(0, 0, 0, 0.5),
                  0 12px 48px rgba(0, 0, 0, 0.3)
                `,
              }}
            />
            {/* Paper texture and aging */}
            <div
              className="absolute inset-0 opacity-50"
              style={{
                backgroundImage: `
                  radial-gradient(ellipse at 10% 10%, rgba(120, 80, 40, 0.08) 0%, transparent 50%),
                  radial-gradient(ellipse at 90% 90%, rgba(120, 80, 40, 0.06) 0%, transparent 40%),
                  radial-gradient(ellipse at 30% 70%, rgba(100, 70, 35, 0.04) 0%, transparent 35%)
                `,
              }}
            />
            {/* Subtle edge darkening */}
            <div
              className="absolute inset-0"
              style={{
                boxShadow: `
                  inset 0 0 60px rgba(120, 80, 40, 0.1)
                `,
              }}
            />

            <form onSubmit={handleSubmitCompose} className="relative px-8 py-10 sm:px-12 sm:py-12">
              {/* Close - subtle corner fold */}
              <button
                type="button"
                onClick={handleCloseCompose}
                className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center opacity-40 hover:opacity-70 transition-opacity"
                style={{ color: '#5c4023' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Body */}
              {composeSuccess ? (
                <div className="text-center py-16">
                  <p className="font-serif text-lg italic" style={{ color: '#4a3520' }}>
                    Your letter has been sealed and sent forth...
                  </p>
                  <p className="font-serif text-sm mt-4" style={{ color: '#7a6040' }}>
                    await a reply in due time
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {composeError && (
                    <p className="font-serif italic text-sm" style={{ color: '#8b4040' }}>
                      {composeError}
                    </p>
                  )}

                  {/* Subject line - like a letter heading */}
                  <div
                    className="border-b pb-4"
                    style={{ borderColor: 'rgba(100, 70, 40, 0.2)' }}
                  >
                    <input
                      type="text"
                      value={composeSubject}
                      onChange={(e) => setComposeSubject(e.target.value)}
                      placeholder="Regarding..."
                      maxLength={200}
                      className="w-full bg-transparent font-display text-lg outline-none border-none ring-0 focus:outline-none focus:border-none focus:ring-0 placeholder:italic placeholder:opacity-40"
                      style={{
                        color: '#3a2510',
                        caretColor: '#3a2510',
                      }}
                      disabled={isComposing}
                    />
                  </div>

                  {/* Letter body - just text, no box */}
                  <div>
                    <textarea
                      value={composeBody}
                      onChange={(e) => setComposeBody(e.target.value)}
                      placeholder="My dearest friend..."
                      rows={14}
                      maxLength={50000}
                      className="w-full bg-transparent resize-none font-serif text-base leading-relaxed outline-none border-none ring-0 focus:outline-none focus:border-none focus:ring-0 placeholder:italic placeholder:opacity-40"
                      style={{
                        color: '#3a2510',
                        caretColor: '#3a2510',
                      }}
                      disabled={isComposing}
                      autoFocus
                    />
                  </div>

                  {/* Footer actions */}
                  <div
                    className="flex items-center justify-between pt-4 border-t"
                    style={{ borderColor: 'rgba(100, 70, 40, 0.15)' }}
                  >
                    <button
                      type="button"
                      onClick={handleCloseCompose}
                      className="px-4 py-2 font-serif text-sm italic rounded transition-all hover:bg-amber-900/10"
                      style={{ color: '#5c4023' }}
                      disabled={isComposing}
                    >
                      set aside
                    </button>
                    <button
                      type="submit"
                      disabled={isComposing || !composeBody.trim()}
                      className="px-5 py-2 font-serif italic rounded transition-all disabled:opacity-30 hover:scale-[1.02]"
                      style={{
                        backgroundColor: 'rgba(92, 61, 46, 0.15)',
                        color: '#3a2510',
                      }}
                    >
                      {isComposing ? 'sealing...' : 'seal & send to the post'}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Instructions Modal */}
      <InstructionsModal forceOpen={showInstructions} onClose={() => setShowInstructions(false)} />
    </PortalScene>
  );
}
